import { Injectable } from '@angular/core';
import { countryConfig } from 'src/country-config/country-config';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AlgoliaService } from '../../networking/services/algolia/algolia.service';
import { AlgoliaSearchInterface } from '../../networking/models/algolia/requests/algolia';
import { environment } from '../../../../environments/environment';
import { Product } from '../../networking/models/product/responses/product.class';
import { LocationManager } from '../location/location.manager';
import { LocalStorageManager } from '../local-storage/local-storage.manager';
import { CacheService } from '../../utils/cache.service';
import { ConfigService } from '../../networking/services-backend-multicountry/config/config.service';
import { BanksList } from '../../networking/services-backend-multicountry/models/properties/banks-list.type';
import { PillReminder } from '../../networking/services-backend-multicountry/models/properties/pill-reminder.type';
import { OptimalRouteForcesToSchedule } from '../../networking/services-backend-multicountry/models/properties/optimal-route-force-schedule.type';
import { ModifaceConfig } from '../../networking/models/modiface/modiface-config';
import { DeliveryTime } from '../../networking/services-backend-multicountry/models/properties/delivery-type-time.type';
import { PropertiesTypes } from '../../networking/services-backend-multicountry/models/properties/propertiesModel.type';
import { ValidateStockAlgoliaUtil } from 'src/app/ui/shared/utilities/validate-stock-algolia/validate-stock-algolia';
import { DYService } from '../../networking/services/dynamic-yield/dynamic-yield.service';

@Injectable({
  providedIn: 'root',
})
export class AlgoliaManager {
  public hitsBrowse: any;
  public hitsPrimeBrowse: any;

  constructor(private algoliaService: AlgoliaService,
    private location: LocationManager,
    private lsm: LocalStorageManager,
    private cacheService: CacheService,
    private configService: ConfigService,
    private validateStockAlgoliaUtil: ValidateStockAlgoliaUtil,
    private dyService: DYService,
  ) { }

  get idCity(): string {
    return this.location.city.id || this.location.city.deliveryType || countryConfig.defaultCity;
  }

  search(
    query: string,
    facets: string,
    page: string = '0',
    filtersAdd?: string,
    sendIdStoreGroup = true,
    indexQuery?,
    sendParams = true,
    hitsPerPage?,
    userSearch?,
    useIdStore?,
  ): Promise<any> {

    const nearbyStores: Array<number> | undefined | null = this.location.getNearbyStores();
    const nerbyStoresFilter = [];
    if (nearbyStores.length > 0) {

      nearbyStores.forEach(store => {
        nerbyStoresFilter.push(`stores_with_stock:${store}`);
      });
    } 

    const data: AlgoliaSearchInterface = {
      query,
      indexName: indexQuery || countryConfig.algolia.index,
      params: {
        hitsPerPage: hitsPerPage || environment.algoliaConfig.hitsPerPage,
        filters: this.getFilters(sendIdStoreGroup, filtersAdd,useIdStore),
        facets: facets,
        page: page,
        optionalFilters: [nerbyStoresFilter],
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'X-Custom-City': this.idCity,
    };

    return this.algoliaService.searchText(data, (userSearch || false), headers).then(({ results }: any) => {
      results[0].hits = results[0].hits.map(item => new Product(item));
      if (results[0]?.hits.length > 0) {
        results[0].hits = this.validateStockAlgoliaUtil.validateStockOfProducts(results[0].hits);
      }
      return results[0];
    })

  }

  serchTutorial(
    query: string,
    facets: string,
    page: string = '0',
    filtersAdd?: string,
    indexQuery?,
    hitsPerPage?
  ): Promise<any> {
    const data: AlgoliaSearchInterface = {
      query,
      indexName: indexQuery || countryConfig.algolia.index,
      params: {
        hitsPerPage: hitsPerPage || environment.algoliaConfig.hitsPerPage,
        filters: filtersAdd,
        facets: facets,
        page: page
      }
    };

    return this.algoliaService.searchText(data).then(({ results }: any) => {
      results[0].hits = results[0].hits.map(item => new Product(item));
      return results[0];
    })
  }

  browse(data, properties?): Promise<any> {
    return this.algoliaService.searchTextBrowse(data).then(() => {
      this.hitsBrowse[0] = this.hitsBrowse[0].hits.map(item => new Product(item));
      return this.hitsBrowse;
    });
  }

  primeBrowse<T>(): Promise<T[]> {
    return this.configService.getProperty<T>('FTD_PRIME_CONFIG_V2')
      .pipe(map((res: T) => [res])).toPromise();
  }

  getConfigOptimalRouteConfig(): Promise<OptimalRouteForcesToSchedule> {
    return this.configService.getProperty<OptimalRouteForcesToSchedule>('OPTIMAL.ROUTE.TEXT.FORCING.SCHEDULE').toPromise();
  }

  getModifaceConfig(): Promise<any> {
    const modifaceConfigFlag = this.lsm.get('modifaceConfigFlag');
    if (!this.cacheService.isExpiredCache(modifaceConfigFlag)) {
        return of(modifaceConfigFlag).toPromise();
    }

    return this.configService.getProperty('MODIFACE.CONFIGURATION.FLAG')
        .pipe(map((config: ModifaceConfig) => {
            const newConfig = this.cacheService.generateNewObjectByHours(config, config.cache_web_by_hours);
            this.lsm.set('modifaceConfigFlag', newConfig);
            return newConfig;
        }))
        .toPromise();
  }

  customTagProduct<CustomTag>(): Promise<CustomTag> {
    return this.configService.getProperty<CustomTag>('CUSTOM.TAG.PRODUCT.CONFIG').toPromise();
  }

  getProducts(objectId): Observable<any> {
    return this.algoliaService.getProduct(objectId);
  }

  getSeoItem(idItem): Observable<any> {
    return this.algoliaService.getSeo(idItem);
  }

  getTutorials(): Promise<any> {
    return this.algoliaService.getObjectsTutorials().pipe(map(res => [res])).toPromise();
  }

  getTutorialLanding(idTutorial): Promise<any> {
    return this.search('', '', '', `objectID:${idTutorial}`, false, 'landing_pages', true);
  }

  private getFilters(sendIdStoreGroup: boolean, filtersAdd?: string, useIdStore = false): string {
    const samplingFilterString = 'NOT rms_class:SAMPLING';
    let getfilters = `${sendIdStoreGroup ? `outofstore:false` : ''} ${filtersAdd}`;
    if (useIdStore) {
      getfilters = `idStoreGroup:${this.location.getIdStoreGroup()} AND ${getfilters}`;
    }
    getfilters = `${getfilters} AND ${samplingFilterString}`;
    return getfilters;
  }

  getPropertiesAlgolia(properties): Observable<any> {
    return this.algoliaService.getPropertiesAlgolia(properties);
  }

  getCustomPropertieAlgolia( properties ): Observable<any> {
    return this.algoliaService.getCustomPropertieAlgolia( properties );
  }

  getLandingsAlgolia( properties ): Observable<any> {
    return this.algoliaService.getLandingsAlgolia(properties);
  }

  getObjectsIds(arrayObjects, customIndex = false): Promise<any> {
    return this.algoliaService.getObjectsIds(arrayObjects, customIndex);
  }

  getObjectsIdsByProperties(arrayObjects): Promise<any> {
    return this.algoliaService.getObjectsIdsByProperties(arrayObjects);
  }

  objectSearch(objectID, index?) {
    return this.algoliaService.objectSearch(objectID, index)
  }

  getPillReminderConfig(): Promise<PillReminder> {
    const pillReminderConfig = this.lsm.get('pillReminderConfig');
    if (!this.cacheService.isExpiredCache(pillReminderConfig)) {
      return of(pillReminderConfig).toPromise();
    }

    return this.algoliaService.getCustomPropertieAlgolia('PILL_REMINDER_CONFIG')
      .pipe(map((config: PillReminder) => {
        const newConfig = this.cacheService.generateNewObjectByHours(config, config.cache_web_by_hours);
        this.lsm.set('pillReminderConfig', newConfig);
        return newConfig;
      }))
      .toPromise();
  }

  getBankList(): Promise<BanksList> {
    return this.algoliaService.getCustomPropertieAlgolia('BANK.LIST.COL').toPromise()
      .then((properties: PropertiesTypes) => {
        if ('banks' in properties) {
          return properties as BanksList;
        }
      });
  }

  getPropertiesByObjectId(objectId): Promise<any> {
    return this.algoliaService.getPropertiesAlgolia(objectId).pipe(map(res => [res])).toPromise();
  }

  getPropertiesFromPostgres<T>(objectId: string): Promise<T> {
    return this.configService.getProperty<T>(objectId).toPromise();
  }

  setContext(type?: string, data?: any | string): void {
    this.dyService.setContext(type, data);
  }
  
}
